import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Table,
  Row,
  Col,
  Spin,
  Divider,
  Typography,
  message
} from 'antd';
import { get } from 'axios';
import { useNavigate } from 'react-router-dom';
import axios from '../../../../helpers/Axios';

const { Link } = Typography;

function VerifyCompany({open, onClose, onVerified}) {
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [companyLabel, setCompanyLabel] = useState('');
  const [secNo, setSecNo] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [disableVerify, setDisableVerify] = useState(true);
  const [showCant, setShowCant] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [searchBy, setSearchBy] = useState('sec_no');
  const [form] = Form.useForm();


  const handleSearchSecNo = async (secNoValue) => {
    const searchTerm = (secNoValue || '').trim();
    setCompanyLabel(searchTerm);
    setLoadingList(true);
    setDisableVerify(true);

    try {
      const res = await get(
        `https://api.sec.gov.ph/ciffs/secno/ciffs_read_sec_no.php?sec_no=${searchTerm}`,
        {
          headers: {
            "X-SEC-API-Key": process.env.REACT_APP_SEC_NO,
          },
        }
      );

      const payload = res.data.data.map((c) => ({
        value: c.sec_no,
        name: c.company_name,
        company_id: c.id,
        label: `${c.sec_no} - ${c.company_name}`,
      }));

      setCompanies(payload);
      setShowCant(true);
    } catch (error) {
      setCompanies([]);
      if (error?.response?.data?.message === "No result found!") {
        setShowCant(false);
        message.warning('No company found. Please try again.');
      } else {
        message.error('Search failed. Please try again.');
      }
    }

    setLoadingList(false);
  };

  const handleSearchCompanyName = async (companyName) => {
    const searchTerm = (companyName || '').trim();

    setCompanyLabel(searchTerm);
    setLoadingList(true);
    setDisableVerify(true);

    if (!searchTerm) {
      setCompanies([]);
      setLoadingList(false);
      return;
    }

    try {
      const res = await get(
        `https://api.sec.gov.ph/ciffs/comname/ciffs_read_com_name.php?
        company_name=${encodeURIComponent(searchTerm)}`,
        {
          headers: {
            'X-SEC-API-Key': process.env.REACT_APP_COMP_NAME,
          },
        }
      );

      const payload = res.data.data.map((company) => ({
        value: company.sec_no,
        name: company.company_name,
        company_id: company.id,
        label: `${company.sec_no} - ${company.company_name}`,
      }));

      setCompanies(payload);
      setShowCant(true);
    } catch (error) {
      setCompanies([]);
      setShowCant(false);
    } finally {
      setLoadingList(false);
    }
  }

  const handleSearch = async (values) => {
    if (searchBy === 'sec_no') {
      await handleSearchSecNo(values.sec_no);
    } else {
      await handleSearchCompanyName(values.company_name);
    }
  };

  const handleSelectCompany = (company) => {
    setCompanyLabel(company.label);
    setSecNo(company.value.replace(/\s/g, ''));
    setCompanyId(company.company_id);
    setSelectedCompanyId(company.company_id);

    // setCompanies([]);
    setDisableVerify(false);
  };

  const handleVerify = async () => {
    setLoadingSubmit(true);

    try {
      const authHeaders = {
        headers: {
          'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
        },
      };

      const res = await axios.post(`https://api.fortisph.space/gateway/authentication/company/enrolled/verify/`,
        { sec_no: encodeURIComponent(secNo) },
        authHeaders,
      );

      console.log('V2 response', res.data);
      if (res.data.company?.enrolled_on) {
        setTimeout(() => {
            message.info("This company is already enrolled.");
        }, 1000);

        setLoadingSubmit(false);
        return;
      }

      const mc28 = await get(
        `https://api.sec.gov.ph/mc28/secno/mc28_sec_no_status.php?sec_no=${encodeURIComponent(secNo)}`,
        {
          headers: {
            "X-SEC-API-Key": process.env.REACT_APP_MC28
          }
        }
      );

      const mc = mc28.data.data[0];

      const mcDetails = {
        name: `${mc.firstname} ${mc.lastname}`,
        official_email: mc.official_email,
        mobile_number: mc.mobile_number.replace(/_/g, ""),
      };

      const profile = await axios.get(
        `https://api.sec.gov.ph/ciffs/general_profile_v2/ciffs_general_profile_v2.php?id=${companyId}`,
        {
          headers: {
            "X-SEC-API-Key": process.env.REACT_APP_GEN_PROFILE2
          }
        }
      );

      const d = profile.data.data[0];

      onVerified({
        verified: true,
        details: {
          companyName: d.company_name,
          secNo: d.sec_no,
          companyType: d.corporation_type,
          companyStatus: d.status_id,
          qualifiedAmnesty: d.qualified_amnesty,
          provinceName: d.principal_province_id,
          exceptional: false,
          amnestyStatus: d.status_id,
        },
        mc28_details: mcDetails,
      });

      onClose();
    } catch (error) {
      message.error("Verification Failed. No MC28 found.");
    }

    setLoadingSubmit(false);
  };

  return (
    <Modal
      open={open}
      title="Verify Company"
      onCancel={onClose}
      footer={null}
      width={650}
    >
      <Form form={form} onFinish={handleSearch} layout="vertical">
        <Form.Item name="sec_no" label="SEC Registration Number">
          <Input placeholder="Enter SEC No." />
        </Form.Item>

        <Form.Item style={{ marginBottom: 24, textAlign: 'right' }}>
          <Button
            htmlType="submit"
            type="primary"
            onClick={() => setSearchBy('sec_no')}
            className="bg-primary-700 "
          >
            Search SEC No.
          </Button>
        </Form.Item>

        <Form.Item name="company_name" label="Company Name">
          <Input placeholder="Enter Company Name" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 12, textAlign: 'right' }}>
          <Button
            htmlType="submit"
            type="primary"
            onClick={() => setSearchBy('company_name')} 
            className="bg-primary-700 "
          >
            Search Company Name
          </Button>
        </Form.Item>
      </Form>

      <Divider />

      {loadingList ? (
        <Spin />
      ) : companies.length ? (
        <Table
          dataSource={companies}
          rowKey="company_id"
          rowClassName={(record) =>
            selectedCompanyId === record.company_id ? 'bg-gray-200 hover:bg-gray-200' : 'cursor-pointer'
          }
          pagination={false}
          columns={[
            {
              title: "Select Company",
              dataIndex: "label",
              render: (text, record) => (
                <Link
                  style={{ marginBottom: 4 }}
                  className="text-green-700 cursor-pointer underline mb-4 inline-block"
                  onClick={() => handleSelectCompany(record)}
                >
                  {text}
                </Link>
              ),
            },
          ]}
        />
      ) : (
        !showCant && (
          <Button
            danger
            block
            onClick={() => onVerified({ exceptional: true })}
          >
            Can't find the Company? Click Here
          </Button>
        ) 
      )}

      {showCant && (
        <>
          <Button
            type="primary"
            disabled={disableVerify}
            className="bg-primary-700 mt-8"
            loading={loadingSubmit}
            onClick={handleVerify}
          >
            Verify Company
          </Button>
          
          <Button className="ml-2" onClick={() => navigate(-1)}>
            Cancel
          </Button>

          {/* <Button
            type="link"
            onClick={() =>
              navigate('/enroll-company', {
                state: {
                  exceptional: true,
                  verified: false,
                },
              })
            }
          >
            Skip verification (dev)
          </Button> */}
        </>
      )}
    </Modal>
  );
}

export default VerifyCompany;